import React, { useRef, useCallback, useEffect, useState, forwardRef } from 'react';
import Node from './Node';
import Connection from './Connection';
import Group from './Group';
import Swimlane from './Swimlane';
import MiniMap from './MiniMap';
import { useDragAndDrop, usePanning } from '../hooks/useDragAndDrop';

const Canvas = forwardRef(({
  nodes,
  connections,
  groups = [],
  swimlanes = [],
  selectedNodeIds = [],
  selectedGroupId,
  selectedConnectionId,
  selectedSwimlaneId,
  connectingFrom,
  canvas,
  onSelectNode,
  onSelectGroup,
  onSelectConnection,
  onSelectSwimlane,
  onUpdateNode,
  onUpdateNodes,
  onUpdateGroup,
  onUpdateSwimlane,
  onDeleteNode,
  onDoubleClickNode,
  onDoubleClickGroup,
  onDoubleClickConnection,
  onDoubleClickSwimlane,
  onReorderSwimlanes,
  onConnect,
  onCanvasClick,
  onZoomChange,
  onPanChange,
  onToggleGrid,
  isDarkMode
}, ref) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [spacePressed, setSpacePressed] = useState(false);
  const [handMode, setHandMode] = useState(false); // Dedicated hand/pan mode
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [dragStartPositions, setDragStartPositions] = useState({});
  const [draggingGroupId, setDraggingGroupId] = useState(null);
  const [groupDragStart, setGroupDragStart] = useState(null);
  // Swimlane state
  const [resizingSwimlane, setResizingSwimlane] = useState(null);
  const [draggingSwimlane, setDraggingSwimlane] = useState(null);

  // Check if panning should be active (space pressed OR hand mode enabled)
  const isPanModeActive = spacePressed || handMode;

  const { zoom, panX, panY, showGrid = true } = canvas;

  // Expose svgRef to parent
  React.useImperativeHandle(ref, () => svgRef.current);

  // Drag and drop for nodes (with multi-select support)
  const { isDragging, draggedNodeId, startDrag, onDrag, endDrag } = useDragAndDrop(
    (nodeId, position) => {
      // If dragging a selected node and multiple are selected, move all
      if (selectedNodeIds.includes(nodeId) && selectedNodeIds.length > 1) {
        const draggedNode = nodes.find(n => n.id === nodeId);
        if (draggedNode && dragStartPositions[nodeId]) {
          const deltaX = position.x - dragStartPositions[nodeId].x;
          const deltaY = position.y - dragStartPositions[nodeId].y;

          const updates = selectedNodeIds.map(id => {
            const startPos = dragStartPositions[id];
            return {
              id,
              changes: {
                x: startPos.x + deltaX,  // Allow any position (infinite canvas)
                y: startPos.y + deltaY
              }
            };
          });

          if (onUpdateNodes) {
            onUpdateNodes(updates);
          }
        }
      } else {
        onUpdateNode(nodeId, position);
      }
    },
    20 // Grid size
  );

  // Panning
  const { isPanning, startPan, onPan, endPan } = usePanning();

  // Store starting positions when drag begins for multi-select
  const handleNodeMouseDown = useCallback((e, nodeId, nodeX, nodeY) => {
    if (selectedNodeIds.includes(nodeId) && selectedNodeIds.length > 1) {
      // Store all selected nodes' starting positions
      const positions = {};
      selectedNodeIds.forEach(id => {
        const node = nodes.find(n => n.id === id);
        if (node) {
          positions[id] = { x: node.x, y: node.y };
        }
      });
      setDragStartPositions(positions);
    }
    startDrag(e, nodeId, nodeX, nodeY);
  }, [selectedNodeIds, nodes, startDrag]);

  // Handle group drag
  const handleGroupMouseDown = useCallback((e, groupId, groupX, groupY) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startMouseX = (e.clientX - rect.left - panX) / zoom;
    const startMouseY = (e.clientY - rect.top - panY) / zoom;

    setDraggingGroupId(groupId);
    setGroupDragStart({
      mouseX: startMouseX,
      mouseY: startMouseY,
      groupX: group.x,
      groupY: group.y,
      nodePositions: group.nodeIds.reduce((acc, nodeId) => {
        const node = nodes.find(n => n.id === nodeId);
        if (node) {
          acc[nodeId] = { x: node.x, y: node.y };
        }
        return acc;
      }, {})
    });
  }, [groups, nodes, panX, panY, zoom]);

  // Handle swimlane resize start
  const handleSwimlaneResizeStart = useCallback((e, swimlaneId) => {
    e.stopPropagation();
    const swimlane = swimlanes.find(s => s.id === swimlaneId);
    if (!swimlane) return;

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startX = (e.clientX - rect.left - panX) / zoom;
    setResizingSwimlane({
      id: swimlaneId,
      startX,
      originalWidth: swimlane.width || 250
    });
  }, [swimlanes, panX, zoom]);

  // Handle swimlane drag start (for reordering)
  const handleSwimlaneDragStart = useCallback((e, swimlaneId, index) => {
    e.stopPropagation();
    const swimlane = swimlanes.find(s => s.id === swimlaneId);
    if (!swimlane) return;

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startX = (e.clientX - rect.left - panX) / zoom;
    setDraggingSwimlane({
      id: swimlaneId,
      index,
      startX,
      originalX: swimlane.x || 0
    });
  }, [swimlanes, panX, zoom]);

  // Handle mouse move for dragging and panning
  const handleMouseMove = useCallback((e) => {
    // Track mouse position for connecting line
    if (connectingFrom) {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        setMousePos({
          x: (e.clientX - rect.left - panX) / zoom,
          y: (e.clientY - rect.top - panY) / zoom
        });
      }
    }

    // Handle swimlane resize
    if (resizingSwimlane) {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const currentX = (e.clientX - rect.left - panX) / zoom;
        const deltaX = currentX - resizingSwimlane.startX;
        const newWidth = Math.max(150, Math.min(500, resizingSwimlane.originalWidth + deltaX));
        onUpdateSwimlane?.(resizingSwimlane.id, { width: Math.round(newWidth) });
      }
      return;
    }

    // Handle swimlane drag for reordering (visual feedback only)
    if (draggingSwimlane) {
      // Could add visual feedback here if needed
      return;
    }

    // Handle group dragging
    if (draggingGroupId && groupDragStart) {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const currentMouseX = (e.clientX - rect.left - panX) / zoom;
        const currentMouseY = (e.clientY - rect.top - panY) / zoom;

        const deltaX = currentMouseX - groupDragStart.mouseX;
        const deltaY = currentMouseY - groupDragStart.mouseY;

        // Snap to grid
        const gridSize = 20;
        const snappedDeltaX = Math.round(deltaX / gridSize) * gridSize;
        const snappedDeltaY = Math.round(deltaY / gridSize) * gridSize;

        // Update group position
        const newGroupX = groupDragStart.groupX + snappedDeltaX;
        const newGroupY = groupDragStart.groupY + snappedDeltaY;
        onUpdateGroup?.(draggingGroupId, { x: newGroupX, y: newGroupY });

        // Move all nodes in the group
        const nodeUpdates = Object.entries(groupDragStart.nodePositions).map(([nodeId, pos]) => ({
          id: parseInt(nodeId),
          changes: {
            x: pos.x + snappedDeltaX,
            y: pos.y + snappedDeltaY
          }
        }));
        if (nodeUpdates.length > 0 && onUpdateNodes) {
          onUpdateNodes(nodeUpdates);
        }
      }
      return;
    }

    if (isDragging) {
      onDrag(e, zoom);
    } else if (isPanning) {
      const newPan = onPan(e);
      if (newPan) {
        onPanChange(newPan);
      }
    }
  }, [isDragging, isPanning, connectingFrom, zoom, panX, panY, onDrag, onPan, onPanChange, draggingGroupId, groupDragStart, onUpdateGroup, onUpdateNodes, resizingSwimlane, draggingSwimlane, onUpdateSwimlane]);

  // Handle mouse up
  const handleMouseUp = useCallback((e) => {
    // Handle swimlane reorder on drop
    if (draggingSwimlane && swimlanes.length > 1) {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const currentX = (e.clientX - rect.left - panX) / zoom;
        // Determine which lane position we're dropping at
        let targetIndex = 0;
        let accumulatedX = 0;
        for (let i = 0; i < swimlanes.length; i++) {
          const laneWidth = swimlanes[i].width || 250;
          if (currentX < accumulatedX + laneWidth / 2) {
            targetIndex = i;
            break;
          }
          accumulatedX += laneWidth;
          targetIndex = i + 1;
        }
        // Clamp to valid range
        targetIndex = Math.max(0, Math.min(swimlanes.length - 1, targetIndex));
        // Only reorder if different position
        if (targetIndex !== draggingSwimlane.index) {
          onReorderSwimlanes?.(draggingSwimlane.index, targetIndex);
        }
      }
    }

    endDrag();
    endPan();
    setDragStartPositions({});
    setDraggingGroupId(null);
    setGroupDragStart(null);
    setResizingSwimlane(null);
    setDraggingSwimlane(null);
  }, [endDrag, endPan, draggingSwimlane, swimlanes, panX, zoom, onReorderSwimlanes]);

  // Handle canvas click
  const handleCanvasClick = useCallback((e) => {
    if (e.target === svgRef.current || e.target.classList.contains('canvas-background')) {
      onCanvasClick?.();
    }
  }, [onCanvasClick]);

  // Handle canvas mouse down for panning
  const handleCanvasMouseDown = useCallback((e) => {
    // Left click with pan mode active OR middle mouse button
    if ((isPanModeActive && e.button === 0) || e.button === 1) {
      e.preventDefault();
      startPan(e, panX, panY);
    }
  }, [isPanModeActive, panX, panY, startPan]);

  // Handle wheel for zooming (Ctrl+scroll) and panning (scroll)
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    if (e.ctrlKey || e.metaKey) {
      // Zoom with Ctrl/Cmd + scroll
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.min(Math.max(zoom + delta, 0.25), 3);
      onZoomChange(newZoom);
    } else {
      // Pan with regular scroll (supports trackpad gestures)
      const newPanX = panX - e.deltaX;
      const newPanY = panY - e.deltaY;
      onPanChange({ panX: newPanX, panY: newPanY });
    }
  }, [zoom, panX, panY, onZoomChange, onPanChange]);

  // Keyboard event handlers for space bar (panning) and H key (hand mode)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      if (e.code === 'Space' && !e.repeat) {
        setSpacePressed(true);
      }

      // H key toggles hand mode
      if (e.key.toLowerCase() === 'h' && !e.repeat) {
        setHandMode(prev => !prev);
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        setSpacePressed(false);
        endPan();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [endPan]);

  // Add event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Get connecting line from source node to mouse
  const getConnectingLine = () => {
    if (!connectingFrom) return null;

    const fromNode = nodes.find(n => n.id === connectingFrom);
    if (!fromNode) return null;

    const fromX = fromNode.x + fromNode.width / 2;
    const fromY = fromNode.y + fromNode.height;

    return (
      <line
        x1={fromX}
        y1={fromY}
        x2={mousePos.x}
        y2={mousePos.y}
        stroke="#3b82f6"
        strokeWidth={2}
        strokeDasharray="5,5"
        pointerEvents="none"
      />
    );
  };

  // Fit to view - zoom and pan to show all nodes
  const fitToView = useCallback(() => {
    if (nodes.length === 0) {
      onZoomChange(1);
      onPanChange({ panX: 0, panY: 0 });
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Calculate bounds of all nodes
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    nodes.forEach(node => {
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x + node.width);
      maxY = Math.max(maxY, node.y + node.height);
    });

    // Add padding
    const padding = 60;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    // Calculate zoom to fit
    const zoomX = containerWidth / contentWidth;
    const zoomY = containerHeight / contentHeight;
    const newZoom = Math.min(Math.max(Math.min(zoomX, zoomY), 0.25), 2);

    // Calculate pan to center content
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const newPanX = (containerWidth / 2) - (centerX * newZoom);
    const newPanY = (containerHeight / 2) - (centerY * newZoom);

    onZoomChange(newZoom);
    onPanChange({ panX: newPanX, panY: newPanY });
  }, [nodes, onZoomChange, onPanChange]);

  // Grid pattern colors
  const gridColor = isDarkMode ? '#374151' : '#e5e7eb';
  const bgColor = isDarkMode ? '#1f2937' : '#f9fafb';

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-hidden relative"
      style={{ cursor: isPanModeActive ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
    >
      <svg
        ref={svgRef}
        className="w-full h-full"
        style={{ backgroundColor: bgColor }}
        onClick={handleCanvasClick}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Definitions */}
        <defs>
          {/* Dot grid pattern */}
          <pattern
            id="dotGrid"
            width={20 * zoom}
            height={20 * zoom}
            patternUnits="userSpaceOnUse"
            x={panX % (20 * zoom)}
            y={panY % (20 * zoom)}
          >
            <circle
              cx={1}
              cy={1}
              r={1}
              fill={gridColor}
            />
          </pattern>

          {/* Arrow marker */}
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#6b7280"
            />
          </marker>

          {/* Selected arrow marker */}
          <marker
            id="arrowhead-selected"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#1d4ed8"
            />
          </marker>
        </defs>

        {/* Background with dot grid */}
        <rect
          className="canvas-background"
          width="100%"
          height="100%"
          fill={showGrid ? "url(#dotGrid)" : bgColor}
        />

        {/* Transformed content group */}
        <g transform={`translate(${panX}, ${panY}) scale(${zoom})`}>
          {/* Swimlanes layer (rendered first so they appear as background) */}
          {swimlanes.length > 0 && (
            <g className="swimlanes-layer">
              {swimlanes.map((swimlane, index) => (
                <Swimlane
                  key={swimlane.id}
                  swimlane={swimlane}
                  index={index}
                  totalLanes={swimlanes.length}
                  canvasHeight={5000}
                  isSelected={selectedSwimlaneId === swimlane.id}
                  onSelect={onSelectSwimlane}
                  onDoubleClick={onDoubleClickSwimlane}
                  onResize={handleSwimlaneResizeStart}
                  onDragStart={handleSwimlaneDragStart}
                />
              ))}
            </g>
          )}

          {/* Groups layer (rendered after swimlanes, before nodes) */}
          <g className="groups-layer">
            {groups.map(group => (
              <Group
                key={group.id}
                group={group}
                isSelected={selectedGroupId === group.id}
                onSelect={onSelectGroup}
                onDoubleClick={onDoubleClickGroup}
                onMouseDown={handleGroupMouseDown}
              />
            ))}
          </g>

          {/* Connections layer */}
          <g className="connections-layer">
            {connections.map(connection => {
              const fromNode = nodes.find(n => n.id === connection.from);
              const toNode = nodes.find(n => n.id === connection.to);

              return (
                <Connection
                  key={connection.id}
                  connection={connection}
                  fromNode={fromNode}
                  toNode={toNode}
                  isSelected={selectedConnectionId === connection.id}
                  onClick={onSelectConnection}
                  onDoubleClick={onDoubleClickConnection}
                />
              );
            })}
          </g>

          {/* Connecting line preview */}
          {getConnectingLine()}

          {/* Nodes layer */}
          <g className="nodes-layer">
            {nodes.map(node => (
              <Node
                key={node.id}
                node={node}
                isSelected={selectedNodeIds.includes(node.id)}
                isConnecting={!!connectingFrom}
                isConnectingFrom={connectingFrom === node.id}
                onSelect={onSelectNode}
                onDoubleClick={onDoubleClickNode}
                onMouseDown={!connectingFrom ? handleNodeMouseDown : undefined}
                onConnect={onConnect}
              />
            ))}
          </g>
        </g>
      </svg>

      {/* Mini Map */}
      <MiniMap
        nodes={nodes}
        connections={connections}
        canvas={canvas}
        onPanChange={onPanChange}
        isDarkMode={isDarkMode}
      />

      {/* Zoom controls */}
      <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2">
        {/* Hand/Pan mode toggle */}
        <button
          onClick={() => setHandMode(!handMode)}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            handMode
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
          }`}
          title={handMode ? 'Exit pan mode (H)' : 'Pan mode - drag to move canvas (H)'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
          </svg>
        </button>
        <div className="w-px h-6 bg-gray-200 dark:bg-gray-600" />
        {/* Grid toggle */}
        <button
          onClick={onToggleGrid}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            showGrid
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500'
          }`}
          title={showGrid ? 'Hide grid' : 'Show grid'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16M6 4v16M12 4v16M18 4v16" />
          </svg>
        </button>
        <div className="w-px h-6 bg-gray-200 dark:bg-gray-600" />
        <button
          onClick={() => onZoomChange(Math.max(zoom - 0.25, 0.25))}
          className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          title="Zoom out"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[60px] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={() => onZoomChange(Math.min(zoom + 0.25, 3))}
          className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          title="Zoom in"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <div className="w-px h-6 bg-gray-200 dark:bg-gray-600 mx-1" />
        {/* Fit to view */}
        <button
          onClick={fitToView}
          className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          title="Fit all nodes in view"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        </button>
        <button
          onClick={() => {
            onZoomChange(1);
            onPanChange({ panX: 0, panY: 0 });
          }}
          className="px-2 h-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm"
          title="Reset to origin (100%)"
        >
          Reset
        </button>
      </div>

      {/* Connecting mode indicator */}
      {connectingFrom && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium">
          Click on target node to connect • Press Escape to cancel
        </div>
      )}

      {/* Multi-select indicator */}
      {selectedNodeIds.length > 1 && (
        <div className="absolute top-4 left-4 bg-blue-500 text-white px-3 py-1.5 rounded-lg shadow-lg text-sm font-medium">
          {selectedNodeIds.length} nodes selected
        </div>
      )}

      {/* Hand mode indicator */}
      {handMode && (
        <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1.5 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
          </svg>
          Pan Mode • Press H to exit
        </div>
      )}

      {/* Navigation hints */}
      <div className="absolute bottom-16 right-4 text-xs text-gray-400 dark:text-gray-500 bg-white/80 dark:bg-gray-800/80 px-2 py-1 rounded">
        Scroll to pan • Ctrl+Scroll to zoom • H for hand mode
      </div>
    </div>
  );
});

Canvas.displayName = 'Canvas';

export default Canvas;
